import {
  AlertTriangle,
  Code,
  History,
  LineChart,
  Minimize2,
  ScrollText,
} from 'lucide-react';
import { useCurrentStateAndParams } from '@uirouter/react';

import LaptopCode from '@/assets/ico/laptop-code.svg?c';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useNamespaceAccessRedirect } from '@/react/kubernetes/namespaces/hooks/useNamespaceAccessRedirect';
import { useEnvironmentObservability } from '@/react/kubernetes/namespaces/queries/useEnvironmentObservability';
import {
  ObservabilityLogs,
  LOGS_TAB_WRAPPER_CLASS,
} from '@/react/kubernetes/namespaces/components/Observability/ObservabilityLogs';
import { ObservabilityMetrics } from '@/react/kubernetes/namespaces/components/Observability/ObservabilityMetrics';

import { PageHeader } from '@@/PageHeader';
import { Tab, WidgetTabs, useCurrentTabIndex } from '@@/Widget/WidgetTabs';
import { Icon } from '@@/Icon';
import { Badge } from '@@/Badge';
import { InlineLoader } from '@@/InlineLoader';

import { EventsDatatable } from '../../components/EventsDatatable';
import { useApplication } from '../queries/useApplication';

import {
  PlacementsDatatable,
  usePlacementTableData,
  usePlacementTableState,
} from './PlacementsDatatable';
import { ApplicationDetailsWidget } from './ApplicationDetailsWidget';
import { ApplicationSummaryWidget } from './ApplicationSummaryWidget';
import { ApplicationContainersDatatable } from './ApplicationContainersDatatable';
import {
  useApplicationEventsTableData,
  useApplicationEventsTableState,
} from './useApplicationEventsTableData';
import { ApplicationYAMLEditor } from './AppYAMLEditor/ApplicationYAMLEditor';
import { useApplicationYAML } from './AppYAMLEditor/useApplicationYAML';

export function ApplicationDetailsView() {
  const stateAndParams = useCurrentStateAndParams();
  const {
    params: { namespace, name, 'resource-type': resourceType },
  } = stateAndParams;
  const environmentId = useEnvironmentId();
  useNamespaceAccessRedirect(namespace, { to: 'kubernetes.applications' });

  // `resourceType` is a URL param and may be stale or absent (e.g. an old
  // bookmark); once the application loads, its actual kind is authoritative
  // so the Logs tab never silently falls back to namespace-wide logs.
  const { data: application } = useApplication(
    environmentId,
    namespace,
    name,
    resourceType
  );
  const applicationKind = application?.kind ?? resourceType;

  const observabilityEnabled = Boolean(
    useEnvironmentObservability(environmentId).data?.enabled
  );

  // placements table data
  const { placementsData, isPlacementsTableLoading, hasPlacementWarning } =
    usePlacementTableData();
  const placementsTableState = usePlacementTableState();

  // events table data
  const { appEventsData, appEventWarningCount, isAppEventsTableLoading } =
    useApplicationEventsTableData();
  const appEventsTableState = useApplicationEventsTableState();

  // load app yaml data early to load from cache later
  useApplicationYAML();

  const tabs: Tab[] = [
    {
      name: 'Application',
      icon: LaptopCode,
      widget: <ApplicationSummaryWidget />,
      selectedTabParam: 'application',
    },
    {
      name: (
        <div className="flex items-center gap-x-2">
          Placement
          {hasPlacementWarning && (
            <Badge type="warnSecondary">
              <Icon icon={AlertTriangle} className="!mr-1" />1
            </Badge>
          )}
        </div>
      ),
      icon: Minimize2,
      widget: (
        <PlacementsDatatable
          hasPlacementWarning={hasPlacementWarning}
          tableState={placementsTableState}
          dataset={placementsData}
          isLoading={isPlacementsTableLoading}
        />
      ),
      selectedTabParam: 'placement',
    },
    {
      name: (
        <div className="flex items-center gap-x-2">
          Events
          {appEventWarningCount >= 1 && (
            <Badge type="warnSecondary">
              <Icon icon={AlertTriangle} className="!mr-1" />
              {appEventWarningCount}
            </Badge>
          )}
        </div>
      ),
      icon: History,
      widget: (
        <EventsDatatable
          dataset={appEventsData}
          tableState={appEventsTableState}
          isLoading={isAppEventsTableLoading}
          data-cy="k8sAppDetail-eventsTable"
        />
      ),
      selectedTabParam: 'events',
    },
    {
      name: 'YAML',
      icon: Code,
      widget: <ApplicationYAMLEditor />,
      selectedTabParam: 'YAML',
    },
    ...(observabilityEnabled
      ? [
          {
            name: 'Metrics',
            icon: LineChart,
            widget: applicationKind ? (
              <ObservabilityMetrics
                environmentId={environmentId}
                namespace={namespace}
                kind={applicationKind}
                name={name}
              />
            ) : (
              <InlineLoader>Loading application...</InlineLoader>
            ),
            selectedTabParam: 'metrics',
          },
          {
            name: 'Logs',
            icon: ScrollText,
            // Wait for the application's kind before querying: rendering the
            // viewer with an undefined kind would fire one unscoped
            // namespace-wide query before the scoped refetch.
            widget: applicationKind ? (
              <ObservabilityLogs
                environmentId={environmentId}
                namespace={namespace}
                kind={applicationKind}
                name={name}
              />
            ) : (
              <InlineLoader>Loading application...</InlineLoader>
            ),
            selectedTabParam: 'logs',
          },
        ]
      : []),
  ];
  const currentTabIndex = useCurrentTabIndex(tabs);

  return (
    <>
      <PageHeader
        title="Application details"
        breadcrumbs={[
          { label: 'Namespaces', link: 'kubernetes.resourcePools' },
          {
            label: namespace,
            link: 'kubernetes.resourcePools.resourcePool',
            linkParams: { id: namespace },
          },
          { label: 'Applications', link: 'kubernetes.applications' },
          name,
        ]}
        reload
      />
      <>
        <WidgetTabs tabs={tabs} currentTabIndex={currentTabIndex} />
        {['logs', 'metrics'].includes(
          tabs[currentTabIndex].selectedTabParam
        ) ? (
          <div className={LOGS_TAB_WRAPPER_CLASS}>
            {tabs[currentTabIndex].widget}
          </div>
        ) : (
          <>
            {tabs[currentTabIndex].widget}
            <ApplicationDetailsWidget />
            <ApplicationContainersDatatable />
          </>
        )}
      </>
    </>
  );
}
